import { ComponentType } from '@angular/cdk/portal';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FeatureToggleCode } from '@core/enums/feature-toggle-code';
import { MeasurementPointIdStatuses } from '@core/enums/measurement-point-id-statuses';
import { TreeItemStatus } from '@core/enums/tree-item-status';
import { DuplicatedCmlLabelError } from '@core/errors/duplicated-cml-label-error';
import { MeasurementHelper } from '@core/helpers/measurement.helper';
import { SurveyMeasurementTableModalInput } from '@core/interfaces/modal-inputs/survey-measurement-table-modal-input';
import { SurveyMeasurement } from '@core/interfaces/survey-measurement';
import { SurveyPoint } from '@core/interfaces/survey-point';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ThicknessService } from '@core/services/thickness.service';
import { UuidService } from '@core/services/uuid.service';
import { CorrosionMonitoringLocationsService as WebCorrosionMonitoringLocationsService } from '@web/services/corrosion-monitoring-locations.service';
import { CorrosionMonitoringLocationsService as MobileCorrosionMonitoringLocationsService } from 'src/app/mobile/services/corrosion-monitoring-locations.service';
import { TextField } from '@shared/interfaces/text-field';
import _ from 'lodash';
import { ConfirmationModalComponent, ConfirmationModalConfig, DialogService, SnackBarService } from 'osc-reference-app-common';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LoggerService } from '@shared/services/logger.service';
import { BluetoothResponse } from '@core/interfaces/bluetooth-response';
import { NativeKeyboardService } from '@shared/services/native-keyboard.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TextFieldComponent } from '@shared/components/text-field/text-field.component';
import { NgClass, NgStyle } from '@angular/common';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule, NgModel } from '@angular/forms';
import { MatBadge } from '@angular/material/badge';
import { OscSpinnerComponent } from '@osc-utils/components/osc-spinner/osc-spinner.component';
import { OscEnvironment } from '@osc-utils/enums/osc-environments';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { SurveyMeasurementTableModalOutput } from '@core/interfaces/modal-outputs/survey-measurement-table-modal-output';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MeasurementDeviceService } from '@core/services/measurement-device.service';
import { BluetoothService } from '@core/services/bluetooth.service';
import { MeasurementUnitTypeService } from '@core/services/measurement-unit-type-service';

@Component({
  selector: 'app-survey-measurement-table',
  templateUrl: './survey-measurement-table.component.html',
  styleUrls: ['./survey-measurement-table.component.scss'],
  imports: [
    CdkDrag,
    CdkDragHandle,
    MatIconButton,
    MatIcon,
    TextFieldComponent,
    MatButton,
    NgClass,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    MatInput,
    FormsModule,
    NgStyle,
    MatBadge,
    MatCheckbox,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    OscSpinnerComponent,
    TranslateModule,
  ],
})
export class SurveyMeasurementTableComponent implements OnInit, OnDestroy {
  readonly #logger = inject(LoggerService);
  public configurationService = inject(ConfigurationService);
  public bottomSheetRef = inject<MatBottomSheetRef<SurveyMeasurementTableComponent, SurveyMeasurementTableModalOutput>>(MatBottomSheetRef, {
    optional: true,
  });
  public dialogRef = inject<MatDialogRef<SurveyMeasurementTableComponent, SurveyMeasurementTableModalOutput>>(MatDialogRef, {
    optional: true,
  });
  public bottomSheetData = inject<SurveyMeasurementTableModalInput>(MAT_BOTTOM_SHEET_DATA, { optional: true });
  public dialogData = inject<SurveyMeasurementTableModalInput>(MAT_DIALOG_DATA, { optional: true });
  readonly #snackBarService = inject(SnackBarService);
  readonly #dialogService = inject(DialogService);
  readonly #webCmlService = inject(WebCorrosionMonitoringLocationsService);
  readonly #mobileCmlService = inject(MobileCorrosionMonitoringLocationsService);
  readonly #thicknessService = inject(ThicknessService);
  readonly #zone = inject(NgZone);
  readonly #spinnerService = inject(SpinnerService);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #bluetoothService = inject(BluetoothService);
  readonly #measurementDeviceService = inject(MeasurementDeviceService);
  readonly #measurementUnitTypeService = inject(MeasurementUnitTypeService);
  readonly #translate = inject(TranslateService);
  readonly #nativeKeyboardService = inject(NativeKeyboardService);
  readonly #uuidService = inject(UuidService);

  public selectedPoint: SurveyPoint;
  public allPoints: Array<SurveyPoint>;
  public allPointLabels: Array<Partial<SurveyPoint>>;
  public readonly = false;
  public isEditMode = false;
  public measurement: string;
  public currentSelectedRow: SurveyMeasurement;
  public taskStatus: TreeItemStatus;
  public notifyInProgress: boolean;
  public treeItemStatus = TreeItemStatus;
  public displayedColumns: Array<string> = ['cml', 'previousThk', 'currentThk', 'comment'];
  public commentRow: SurveyMeasurement;
  public measurementPointIdStatuses = MeasurementPointIdStatuses;
  public cmlField: TextField;
  public alarmStage: 'confirm' | 'comment';
  public spinnerId = 'measurement-dialog-spinner';
  public featureToggleCode = FeatureToggleCode;
  public readonly idMaxLength = 20;

  // THICKNESS
  public thicknessSubscription: Subscription;

  public onDataFrom38DLReceivedSubscription: Subscription;
  public commentValidation = true;

  #onUoMFrom38DLReceivedSubscription: Subscription;
  #commentRowChange$: BehaviorSubject<SurveyMeasurement> = new BehaviorSubject<SurveyMeasurement>(null);
  #surveyData: Array<SurveyMeasurement>;
  #isThicknessCapturingInProgress: boolean;
  #editModeBackup: Array<SurveyMeasurement>;
  #cmlLabelBackup: string;
  #readonlyDisplayedColumns: Array<string> = ['cml', 'previousThk', 'currentThk', 'comment'];
  #bluetoothData: BluetoothResponse;
  #commentRowSubscription: Subscription;
  #unitOfMeasurement: string | null = null;
  #commentChanging = false;
  readonly #maxMeasurementIntegerDigits = 5;
  readonly #maxMeasurementFractionalDigits = 4;
  readonly #maxThicknessMeasurement = 100_000;
  readonly #$destroy = new Subject<void>();
  readonly #data: SurveyMeasurementTableModalInput;

  public canEdit = computed(() => {
    const canShowEditControls = this.configurationService.isMobileApp() || this.configurationService.isWebApp();
    const isTaskLockBlockingWebEdit =
      this.configurationService.isWebApp() &&
      this.#data.disabled &&
      (this.#data.taskStates?.some(state => state.type === 'Locked') ?? false);
    const isTaskStatusBlockingWebEdit = this.configurationService.isWebApp() && this.#data.taskStatus !== TreeItemStatus.Planning;
    return canShowEditControls && !isTaskLockBlockingWebEdit && !isTaskStatusBlockingWebEdit;
  });

  public constructor() {
    this.#data = this.bottomSheetData || this.dialogData;
  }

  public get disableComments(): boolean {
    return this.#data.disabled;
  }

  public get comments(): TextField[] {
    this.selectedPoint.ids = this.dataSource;
    return this.selectedPoint.ids.map(id => ({
      maxCountOfLines: 4,
      id: id.id,
      label: this.#translate.instant('surveyMeasurementTable.commentsLabel'),
      placeholder: this.#translate.instant('surveyMeasurementTable.commentsPlaceholder'),
      value: id.comment,
      // Set the debounce time to 300ms instead of the default 1000ms.
      // If the user is quick enough to save before the debounce time, the note will be saved as the old note
      // In edit mode, the debounce time is not needed
      timeForEvent: 300,
      maxLength: 150,
    }));
  }

  public get dataSource(): Array<SurveyMeasurement> {
    return this.#surveyData;
  }

  public get isSaveDisabled() {
    return (
      this.dataSource?.some(itm => !itm.id?.trim()) ||
      !this.selectedPoint?.label?.trim() ||
      this.checkForDuplicates(this.dataSource ?? [], 'id') ||
      this.#commentChanging
    );
  }

  public set dataSource(value: Array<SurveyMeasurement>) {
    this.#surveyData = value;
    this.#surveyData.forEach(itm => {
      if (itm.status === MeasurementPointIdStatuses.Skipped) {
        itm.isSkipped = true;
      }
      itm.comment ??= '';
    });
  }

  public ngOnInit() {
    this.selectedPoint = this.#data.point;
    this.setCmlField();
    this.allPoints = this.#data.allPoints;
    this.allPointLabels = this.#data.allPointLabels;
    this.readonly = this.#data.readonly && environment.name !== OscEnvironment.Simulator;
    this.dataSource = structuredClone(this.selectedPoint.ids);
    this.sortDataSource();
    let unitOfMeasurementData = '';
    const pt = this.allPoints?.find(p => p.ids?.find(id => id.unitOfMeasurement?.length > 0));
    if (pt) {
      unitOfMeasurementData = pt.ids[0]?.unitOfMeasurement;
    }
    this.dataSource.forEach(data => (data.unitOfMeasurement = unitOfMeasurementData));
    this.taskStatus = this.#data.taskStatus;
    this.#commentRowSubscription = this.#commentRowChange$.pipe(delay(20)).subscribe(commentRow => {
      this.#zone.run(() => {
        this.commentRow = commentRow;
        this.alarmStage = 'confirm';
        this.#cd.detectChanges();
      });
    });

    if (this.readonly) {
      this.displayedColumns = this.#readonlyDisplayedColumns;
    }
    if (this.configurationService.isMobileApp() || environment.name === OscEnvironment.Simulator) {
      if (this.#data.editCmlEnabled) {
        this.displayedColumns.push('delete');
      }
      this.#bluetoothData = this.#bluetoothService.getBluetoothResponse();
      this.thicknessSubscription = this.#measurementDeviceService.isAwaitingForThicknessResult().subscribe(isAwaitingForThicknessResult => {
        this.#isThicknessCapturingInProgress = isAwaitingForThicknessResult;
        this.#cd.detectChanges();
      });
      if (this.#bluetoothData) {
        this.onDataNotifiedUnitOfMeasurement();
        this.#measurementDeviceService.sendUnitOfMeasurementRequest();
      }
    }
    if (this.selectedPoint && this.selectedPoint.ids && this.selectedPoint.ids[0]) {
      this.currentSelectedRow = this.getSelectedId(this.selectedPoint);
    }

    if (this.#data.addCustomCML) {
      this.toggleEditMode();
      this.addMeasurementPoint();
    }

    this.#nativeKeyboardService.autoScrollToElementIfHiddenByKeyboard().pipe(takeUntil(this.#$destroy)).subscribe();
  }

  public ngOnDestroy(): void {
    this.thicknessSubscription?.unsubscribe();
    this.onDataFrom38DLReceivedSubscription?.unsubscribe();
    this.#onUoMFrom38DLReceivedSubscription?.unsubscribe();
    this.#measurementDeviceService.dataCaptureStop();
    this.#commentRowSubscription?.unsubscribe();
    this.#$destroy.next();
    this.#$destroy.complete();
  }

  public async retry(row: SurveyMeasurement): Promise<void> {
    try {
      this.commentRow = null;
      const selectedMeasurementPointId = this.selectedPoint.ids.find(i => i.measurementPointId === row.measurementPointId);
      const measurementPointId = this.allPoints
        .find(p => p.id === this.selectedPoint.id)
        .ids.find(i => i.measurementPointId === row.measurementPointId);
      this.setMeasurementPointId(row);
      this.setMeasurementPointId(measurementPointId);
      this.setMeasurementPointId(selectedMeasurementPointId);
      this.updateAllPointsReference();
      await firstValueFrom(
        this.#mobileCmlService.updateCML(this.#data.metadataId, this.selectedPoint, this.#data.taskId, this.#data.alarmConfig),
      );
    } catch (err) {
      this.#logger.error('surveyMeasurementTable.retry', err);
    }
  }

  public keepThisReading(row: SurveyMeasurement): void {
    this.alarmStage = 'comment';
    row.comment = '';
  }

  public closeDialog(): void {
    const shouldDeleteUnsavedCustomCmlOnWeb = this.isEditMode && this.#data.addCustomCML && this.configurationService.isWebApp();

    if (this.isEditMode) {
      if (!shouldDeleteUnsavedCustomCmlOnWeb) {
        this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.onCloseSaveValidationError'));
        return;
      }

      this.closeEditMode();
    }
    if (this.#data.allPointLabels.filter(p => p.label === this.selectedPoint.label).length > 1) {
      this.selectedPoint.label = this.#cmlLabelBackup;
    }
    this.validateIsCommentEmptyInAlarmOrSkipped(this.dataSource).then(res => {
      if (!res) {
        this.commentValidation = false;
        const modalOutput = shouldDeleteUnsavedCustomCmlOnWeb ? { deleteCmlID: this.selectedPoint.id } : undefined;
        if (this.configurationService.isMobileApp()) {
          this.bottomSheetRef.dismiss(modalOutput);
        } else {
          this.dialogRef.close(modalOutput);
        }
      } else {
        this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.onCommentValidationError'));
      }
    });
  }

  public openComment(row: SurveyMeasurement): void {
    this.alarmStage = 'comment';
    this.commentRow = this.commentRow === row ? null : row;
  }

  public cmlFieldLabelChanged(value: string): void {
    this.selectedPoint.label = value;
    this.#cd.detectChanges();
  }

  public getIdEditable(): boolean {
    return !(this.isEditMode && this.selectedPoint.isCustom);
  }

  public selectNextRow(skipThicknessInProgressCheck?: boolean): void {
    if (this.#isThicknessCapturingInProgress && !skipThicknessInProgressCheck) {
      this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.selectNextRowThicknessCapturingInProgressError'));
      return;
    }
    let indexOfCurrentSelected = this.selectedPoint.ids.findIndex(i => i === this.currentSelectedRow);
    let selected = false;
    do {
      indexOfCurrentSelected++;
      if (indexOfCurrentSelected < this.selectedPoint.ids.length) {
        const currentRowToCheck = this.selectedPoint.ids[indexOfCurrentSelected];
        if (this.canRowBeSelected(currentRowToCheck)) {
          this.currentSelectedRow = this.selectedPoint.ids[indexOfCurrentSelected];
          selected = true;
          this.changeTableRowSelection(this.currentSelectedRow);
        }
      } else {
        selected = true;
        this.currentSelectedRow = null;
      }
    } while (!selected);
  }

  public isSelected(row: SurveyMeasurement): boolean {
    return row?.measurementPointId === this.currentSelectedRow?.measurementPointId;
  }

  public isSkipped(row: SurveyMeasurement): boolean {
    return row.status === MeasurementPointIdStatuses.Skipped;
  }

  public changeTableRowSelection(row: SurveyMeasurement): void {
    if (!this.#isThicknessCapturingInProgress && !this.isSkipped(row)) {
      this.currentSelectedRow = row;
    }
  }

  public isThicknessMeasurementInProgress(row: SurveyMeasurement): boolean {
    return this.isSelected(row) && this.#isThicknessCapturingInProgress;
  }

  public previousCml(): void {
    if (this.#isThicknessCapturingInProgress) {
      this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.previousCmlThicknessCapturingInProgressError'));
      return;
    }
    const indexOfSelected: number = this.allPoints.findIndex(p => p === this.selectedPoint);

    if (indexOfSelected > 0) {
      this.selectedPoint = this.allPoints[indexOfSelected - 1];
      this.setCurrentPoint();
    }
  }

  /* for text, Angular may not detect the changes until the input loses focus or explicitly triggers the change detection.
     To address this, we use (input) event
     explicitly to detect changes immediately for all input types. */
  public onMeasurmentIdChange(event: Event, row: SurveyMeasurement): void {
    const value = (event.target as HTMLInputElement).value;
    row.id = value;
  }

  public onMeasurementBeforeInput(event: InputEvent): void {
    if (!event.data) {
      return;
    }

    const input = event.target as HTMLInputElement;
    let selectionStart = input.value.length;
    let selectionEnd = input.value.length;
    try {
      selectionStart = input.selectionStart ?? selectionStart;
      selectionEnd = input.selectionEnd ?? selectionEnd;
    } catch {
      // Selection APIs are unavailable for number inputs in some browsers; mobile entry appends text.
    }
    const proposedValue = `${input.value.slice(0, selectionStart)}${event.data}${input.value.slice(selectionEnd)}`;
    const [integerPart, decimalPart] = proposedValue.split('.');
    const isDecimalValue = /^\d*(\.\d*)?$/.test(proposedValue);
    if (
      !isDecimalValue ||
      integerPart.length > this.#maxMeasurementIntegerDigits ||
      decimalPart?.length > this.#maxMeasurementFractionalDigits
    ) {
      event.preventDefault();
    }
  }

  public onMeasurementChange(model: NgModel, row: SurveyMeasurement): void {
    const value = model.value as string | number | null;
    const numericValue = value === null || value === '' ? NaN : Number(value);
    if (Number.isNaN(numericValue)) {
      // The input is a text field, so [(ngModel)] has already written the raw string into row.measurement.
      // It must be reset to null, otherwise an empty or partial entry (e.g. '' or '.') reaches the CML sync
      // payload and the backend rejects the whole sync event when deserializing it into a double.
      row.measurement = null;
      return;
    }
    const finalValue = this.clampMeasurement(numericValue);
    // setValue drives the input through Angular, so the displayed text stays in sync even when
    // the finalValue value equals the current model value (which [(ngModel)] alone would not repaint).
    if (finalValue !== numericValue) {
      model.control.setValue(finalValue);
    }
    row.measurement = finalValue;
  }

  public deleteMeasurementId(row: SurveyMeasurement): void {
    const data: ConfirmationModalConfig = {
      message: this.#translate.instant('surveyMeasurementTable.deleteMeasurementIdMessage'),
      confirmButtonText: this.#translate.instant('surveyMeasurementTable.deleteMeasurementIdConfirm'),
      declineButtonText: this.#translate.instant('surveyMeasurementTable.deleteMeasurementIdDecline'),
      title: null,
    };
    const dialogRef: MatDialogRef<ConfirmationModalComponent, boolean> = this.#dialogService.openDialog(
      ConfirmationModalComponent as ComponentType<ConfirmationModalComponent>,
      data,
    );

    dialogRef.afterClosed().subscribe(canDelete => {
      if (canDelete) {
        const idsBeforeDelete: SurveyMeasurement[] = structuredClone(this.dataSource);
        const selectedCml: SurveyPoint = structuredClone(this.selectedPoint);
        let cml: SurveyPoint;
        _.remove(this.dataSource, i => i === row);
        this.dataSource = structuredClone(this.dataSource);
        if (this.#editModeBackup.findIndex(i => i.measurementPointId === row.measurementPointId) > -1) {
          firstValueFrom(this.#mobileCmlService.deleteMeasurement(row.measurementPointId, this.#data.taskId))
            .then(async () => {
              _.remove(this.selectedPoint.ids, i => i.measurementPointId === row.measurementPointId);
              cml = this.allPoints.find(i => i.id === this.selectedPoint.id);
              _.remove(cml.ids, i => i.measurementPointId === row.measurementPointId);
              this.updateDataArray();
              MeasurementHelper.setMeasurementStatus(this.selectedPoint, this.#data.alarmConfig);
              await firstValueFrom(
                this.#mobileCmlService.updateCMLStatus({ label: this.selectedPoint.label }, this.selectedPoint.measurementStatus),
              );
            })
            .catch(err => {
              this.#logger.error('Error deleting measurement id', err);
              if (this.selectedPoint.id === selectedCml.id) {
                this.selectedPoint.ids = idsBeforeDelete;
                this.dataSource = idsBeforeDelete;
              }
              cml = this.allPoints.find(i => i.id === selectedCml.id);
              cml.ids = idsBeforeDelete;
              this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.deleteMeasurementIdError'));
            })
            .finally(() => {
              this.updateAllPointsReference();
              this.selectNextRow(true);
            });
        }
      }
    });
  }

  public formatMeasurement(measurement: number): string {
    return measurement === 0 || measurement === null ? '-.----' : measurement.toFixed(3).toString();
  }

  public formatPreviousMeasurement(previousMeasurement: number): string {
    return previousMeasurement === 0 || previousMeasurement === null ? '0.0000' : previousMeasurement.toFixed(3).toString();
  }

  public isMeasurementInAlarmState(measurement: number, previousMeasurement: number): boolean {
    return MeasurementHelper.isMeasurementInAlarmState(measurement, previousMeasurement, this.#data.alarmConfig);
  }

  public isCommentEmptyInAlarmStateOrSkipped(measurement: number, previousMeasurement: number, comment: string, status: string): string {
    if (
      MeasurementHelper.isMeasurementInAlarmState(measurement, previousMeasurement, this.#data.alarmConfig) &&
      this.commentValidation &&
      (comment === '' || comment === null)
    ) {
      return 'red';
    } else if ((comment === '' || comment === null) && status === MeasurementPointIdStatuses.Skipped) {
      return 'red';
    }
  }

  public addMeasurementPoint(): void {
    this.dataSource.push({
      id: '',
      measurement: 0,
      measurementPointId: this.#uuidService.generate(),
      previousMeasurement: 0,
      status: '',
      latitude: 0,
      longitude: 0,
      serialNumber: '',
      editing: true,
      isSkipped: false,
      comment: null,
      unitOfMeasurement: '',
      isCustom: true,
    });
    this.dataSource = structuredClone(this.dataSource);
  }

  public toggleEditMode(): void {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.commentRow = null;
      this.#editModeBackup = structuredClone(this.dataSource);
      this.#cmlLabelBackup = this.selectedPoint.label;
      this.dataSource?.forEach(itm => (itm.editing = true));
    } else if (this.validateCMLLabel()) {
      this.#spinnerService.show(this.spinnerId);
      let unitOfMeasurementData = '';
      const pt = this.allPoints.find(p => p.ids.find(id => id.unitOfMeasurement?.length > 0));
      if (pt) {
        unitOfMeasurementData = pt.ids[0]?.unitOfMeasurement;
      }
      this.dataSource.forEach(data => (data.unitOfMeasurement = unitOfMeasurementData));
      this.sortDataSource();
      this.selectedPoint.ids = structuredClone(this.dataSource);
      if (this.currentSelectedRow?.measurement) {
        const measurement = this.selectedPoint.ids.find(
          meaPoint => meaPoint?.measurementPointId === this.currentSelectedRow?.measurementPointId,
        );
        if (measurement) {
          const isInAlarm = this.isMeasurementInAlarmState(
            this.currentSelectedRow.measurement,
            this.currentSelectedRow.previousMeasurement,
          );
          measurement.status = isInAlarm ? MeasurementPointIdStatuses.CompleteWithAlarm : MeasurementPointIdStatuses.Complete;
        }
      }
      const service = this.configurationService.isMobileApp() ? this.#mobileCmlService : this.#webCmlService;
      firstValueFrom(service.updateCML(this.#data.metadataId, this.selectedPoint, this.#data.taskId, this.#data.alarmConfig))
        .then(res => {
          this.dataSource = res.ids;
          this.selectedPoint = res;
          this.updateDataArray();
          this.#spinnerService.hide(this.spinnerId);
          this.#zone.run(() => {
            if (this.isMeasurementInAlarmState(this.currentSelectedRow?.measurement, this.currentSelectedRow?.previousMeasurement)) {
              this.#commentRowChange$.next(this.currentSelectedRow);
            }
          });
        })
        .catch(err => {
          this.#logger.error('Error adding measurement', err);
          if (err instanceof DuplicatedCmlLabelError) {
            this.selectedPoint.label = '';
            this.#snackBarService.error(err.message);
          } else {
            this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.editModeError'));
          }
          this.dataSource.splice(this.dataSource.length - 1, 1);
          this.dataSource = structuredClone(this.dataSource);
          this.#spinnerService.hide(this.spinnerId);
        })
        .finally(() => {
          this.#cd.detectChanges();
          this.updateAllPointsReference();
        });
      this.closeEditMode();
    }
  }

  public cancelEdit(): void {
    if (this.#data.addCustomCML) {
      if (this.configurationService.isMobileApp()) {
        this.bottomSheetRef.dismiss({ deleteCmlID: this.selectedPoint.id });
      } else {
        const dialogRef = this.dialogRef;
        if (dialogRef === null) {
          throw new Error('Dialog reference is required to close custom CML edit on web');
        }
        dialogRef.close({ deleteCmlID: this.selectedPoint.id });
      }
      return;
    }
    this.restoreEditBackup();
    this.closeEditMode();
  }

  public canEditMeasurementId(row: SurveyMeasurement): boolean {
    return this.isEditMode && row.isCustom;
  }

  public canEditMeasurementValue(): boolean {
    return this.isEditMode && this.configurationService.isMobileApp();
  }

  public canDeleteRow(row: SurveyMeasurement): boolean {
    const measurementCheck = (row.measurement === 0 || row.measurement === null) && row.previousMeasurement === 0;
    const editModeCheck = this.isEditMode && measurementCheck && row.isCustom;
    const rowLengthCheck = this.dataSource.length > 1;
    return editModeCheck && rowLengthCheck;
  }

  public canEditComment(row: SurveyMeasurement): boolean {
    return row.editing && row.id === '' && (row.serialNumber === '' || row.serialNumber === undefined || row.measurement === 0);
  }

  public onCommentChanging() {
    this.#commentChanging = true;
  }

  public async onCommentChanged(comment: string, index: number, row: SurveyMeasurement): Promise<void> {
    const oldComment = row.comment;
    if (this.isEditMode) {
      this.dataSource[index].comment = comment;
    } else {
      try {
        if (row.measurementPointId) {
          const alarmStatus = this.isMeasurementInAlarmState(row.measurement, row.previousMeasurement);
          let status = MeasurementPointIdStatuses.Pending;
          if (alarmStatus) {
            status = MeasurementPointIdStatuses.CompleteWithAlarm;
          } else if (this.dataSource[index].isSkipped) {
            status = MeasurementPointIdStatuses.Skipped;
          }
          const service = this.configurationService.isMobileApp() ? this.#mobileCmlService : this.#webCmlService;
          await firstValueFrom(
            service.editMeasurementComment(
              { cmlId: this.selectedPoint.id, measurementId: row.measurementPointId, comment, status },
              this.#data.taskId,
            ),
          );
          row.comment = comment;
          const point = this.allPoints.find(i => i.id === this.selectedPoint.id);
          const pointId = point.ids[index];
          pointId.comment = comment;
          this.dataSource[index].comment = comment;
          this.updateAllPointsReference();
          await firstValueFrom(service.updateCML(this.#data.metadataId, this.selectedPoint, this.#data.taskId, this.#data.alarmConfig));
          this.#cd.detectChanges();
        } else {
          this.dataSource[index].comment = comment;
        }
      } catch (err) {
        row.comment = oldComment;
        this.#logger.error('Error editing comment', err);
        this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.onCommentChangedError'));
      }
    }

    this.#commentChanging = false;
  }

  public async onSkippedChanged(event: MatCheckboxChange, index: number, row: SurveyMeasurement): Promise<void> {
    row.isSkipped = event.checked;
    row.status = event.checked ? MeasurementPointIdStatuses.Skipped : MeasurementPointIdStatuses.Pending;

    if (this.isEditMode) {
      return;
    }

    const selectedPointId = this.selectedPoint.id;
    const selectedMeasurementPointId = row.measurementPointId;
    try {
      const point = this.allPoints.find(i => i.id === this.selectedPoint.id);
      point.ids[index].status = row.status;
      const service = this.configurationService.isMobileApp() ? this.#mobileCmlService : this.#webCmlService;
      await firstValueFrom(service.updateCML(this.#data.metadataId, this.selectedPoint, this.#data.taskId, this.#data.alarmConfig));
      this.updateAllPointsReference();
      this.#cd.detectChanges();
    } catch (ex) {
      // Section necessary for Online call as this logic is used on the web.
      this.#logger.error('surveyMeasurementTable.onSkippedChanged', ex);
      const point = this.allPoints.find(i => i.id === selectedPointId);
      const id = point.ids.find(i => i.measurementPointId === selectedMeasurementPointId);
      row.isSkipped = !event.checked;
      id.status = row.isSkipped ? MeasurementPointIdStatuses.Skipped : MeasurementPointIdStatuses.Pending;
      this.updateAllPointsReference();
      this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.onSkippedChangedError'));
      this.#cd.detectChanges();
    }
  }

  public updateAllPointsReference(): void {
    MeasurementHelper.setMeasurementStatus(this.selectedPoint, this.#data.alarmConfig);
    const selectedPointIndex = this.allPoints.findIndex(point => point.id === this.selectedPoint.id);
    if (selectedPointIndex > -1) {
      this.allPoints[selectedPointIndex].measurementStatus = this.selectedPoint?.measurementStatus;
    }
  }

  private validateCMLLabel(): boolean {
    const allPointLabels = this.#data.allPointLabels.filter(p => p.label !== '');
    const labelCount = allPointLabels.filter(p => p.id !== this.selectedPoint.id && p.label === this.selectedPoint.label).length;
    let isValid = true;
    if (labelCount !== 0 || this.selectedPoint.label === undefined) {
      isValid = false;
      this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.cmlLabelError'));
      this.selectedPoint.label = '';
      this.setCmlField();
    }
    return isValid;
  }

  private async validateIsCommentEmptyInAlarmOrSkipped(surveyMeasurement: SurveyMeasurement[]): Promise<boolean> {
    surveyMeasurement.forEach(ele => {
      const alarmStatus = this.isMeasurementInAlarmState(ele.measurement, ele.previousMeasurement);
      const measurementStatus = ele.status === MeasurementPointIdStatuses.Skipped;
      const comment = !!(ele.comment === null || ele.comment === '');
      if ((alarmStatus && comment) || (measurementStatus && comment)) {
        return true;
      }
    });
    return false;
  }

  private onDataNotifiedUnitOfMeasurement(): void {
    this.#onUoMFrom38DLReceivedSubscription = this.#measurementUnitTypeService.onDataFrom38DLReceived().subscribe(response => {
      if (response) {
        this.#unitOfMeasurement = response;
        this.onDataNotifiedMeasurement();
      } else {
        this.#measurementDeviceService.sendUnitOfMeasurementRequest();
      }
    });
  }

  private onDataNotifiedMeasurement(): void {
    this.onDataFrom38DLReceivedSubscription = this.#measurementDeviceService.onDataFrom38DLReceived().subscribe(async response => {
      if (response.data === null) {
        this.#measurementDeviceService.thicknessDataReceived();
        return;
      }
      if (this.#isThicknessCapturingInProgress || (response.data as number) <= 0 || !this.currentSelectedRow) {
        return;
      }
      this.#measurementDeviceService.thicknessCapturingStarted();
      try {
        const oldThicknessMeasurement = Number(this.currentSelectedRow.measurement);
        const oldSerialNumber = this.currentSelectedRow.serialNumber ? this.currentSelectedRow.serialNumber : '';
        const serialNumber = localStorage.getItem('serialnumber');
        const thicknessReading = Number(response.data);
        if (this.currentSelectedRow.unitOfMeasurement?.length === 0) {
          const pt = this.allPoints.find(p => p.ids.find(id => id.unitOfMeasurement?.length > 0));
          if (pt) {
            this.currentSelectedRow.unitOfMeasurement = pt.ids[0]?.unitOfMeasurement;
          } else {
            this.currentSelectedRow.unitOfMeasurement = this.#unitOfMeasurement;
          }
        }

        if (this.currentSelectedRow.unitOfMeasurement !== this.#unitOfMeasurement) {
          this.onReadingUoMError();
          return;
        }
        const measurement = thicknessReading >= this.#maxThicknessMeasurement ? 0 : this.clampMeasurement(thicknessReading);
        this.currentSelectedRow.measurement = Number.isNaN(measurement) ? 0 : measurement;
        this.currentSelectedRow.serialNumber = serialNumber;
        this.setMeasurementForSelectedPoint();
        const selectNextInput = () => {
          if (this.isMeasurementInAlarmState(this.currentSelectedRow.measurement, this.currentSelectedRow.previousMeasurement)) {
            this.#commentRowChange$.next(this.currentSelectedRow);
          } else {
            this.selectNextRow(true);
          }
        };
        if (this.isEditMode) {
          selectNextInput();
          return; // In edit mode, we want the user to press save
        }

        const saveMeasurementResult = await this.#thicknessService.saveThickness(
          this.currentSelectedRow,
          measurement,
          oldThicknessMeasurement,
          this.selectedPoint,
          this.#data.alarmConfig,
          this.#data.metadataId,
          this.#data.taskId,
        );
        if (saveMeasurementResult.rowSelectionAction === 'selectNext') {
          this.currentSelectedRow.measurement = saveMeasurementResult.measurementToSet;
          this.currentSelectedRow.serialNumber = serialNumber;
          this.getCurrentSelectedMeasurementId().measurement = saveMeasurementResult.measurementToSet;
          this.getCurrentSelectedMeasurementId().serialNumber = serialNumber;
          this.updateAllPointsReference();
          selectNextInput();
        } else if (saveMeasurementResult.rowSelectionAction === 'stayOnCurrent') {
          this.currentSelectedRow.measurement = oldThicknessMeasurement;
          this.currentSelectedRow.serialNumber = oldSerialNumber;
          this.getCurrentSelectedMeasurementId().measurement = oldThicknessMeasurement;
          this.getCurrentSelectedMeasurementId().serialNumber = oldSerialNumber;
          this.setMeasurementForSelectedPoint();
        }
        this.updateDataArray();
      } finally {
        this.#measurementDeviceService.thicknessDataReceived();
        this.#cd.detectChanges();
      }
    });
  }

  private setMeasurementPointId(
    measurementPoint: SurveyMeasurement,
    status: MeasurementPointIdStatuses = MeasurementPointIdStatuses.Pending,
    value: number = 0,
  ): void {
    measurementPoint.measurement = value;
    measurementPoint.status = status;
  }

  private clampMeasurement(value: number): number {
    const [integerPart, fractionalPart = ''] = Math.max(value, 0).toString().split('.');
    return Number(
      `${integerPart.slice(-this.#maxMeasurementIntegerDigits)}.${fractionalPart.slice(0, this.#maxMeasurementFractionalDigits)}`,
    );
  }

  private updateDataArray() {
    this.#data.point.ids = structuredClone(this.selectedPoint.ids);
  }

  private closeEditMode() {
    this.isEditMode = false;
    this.selectedPoint.ids?.forEach(id => (id.editing = false));
    this.dataSource?.forEach(itm => {
      if (itm.id) {
        itm.editing = false;
      }
    });
    this.#data.addCustomCML = false;
  }

  private restoreEditBackup(): void {
    this.dataSource = structuredClone(this.#editModeBackup);
    this.selectedPoint.label = this.#cmlLabelBackup;
  }

  private getSelectedId(selectedPoint: SurveyPoint): SurveyMeasurement {
    const firstPointWithoutResults = selectedPoint.ids.find(id => id.measurement === 0);
    return firstPointWithoutResults ?? selectedPoint.ids[0];
  }

  private setCurrentPoint(): void {
    this.dataSource = this.selectedPoint.ids;
    const notSkipped = this.selectedPoint.ids.filter(i => !this.isSkipped(i));
    this.currentSelectedRow = notSkipped.length > 0 ? notSkipped[0] : null;
    this.setCmlField();
  }

  private canRowBeSelected(row: SurveyMeasurement): boolean {
    return row.status === MeasurementPointIdStatuses.Pending && (row.measurement === null || row.measurement === 0);
  }

  private getCurrentSelectedMeasurementId(): SurveyMeasurement {
    return this.dataSource.find(i => i.measurementPointId === this.currentSelectedRow?.measurementPointId);
  }

  private checkForDuplicates(array, keyName): boolean {
    return new Set(array.map(item => item[keyName])).size !== array.length;
  }

  private setMeasurementForSelectedPoint(): void {
    const selected = this.selectedPoint.ids.find(i => i.measurementPointId === this.currentSelectedRow.measurementPointId);
    if (!selected) {
      return;
    }
    selected.measurement = this.currentSelectedRow.measurement;
    selected.serialNumber = localStorage.getItem('serialnumber');
    this.updateAllPointsReference();
  }

  private setCmlField(): void {
    this.cmlField = {
      maxCountOfLines: 1,
      prefix: this.#translate.instant('surveyMeasurementTable.cml'),
      id: `cmlField`,
      label: ``,
      placeholder: null,
      value: this.selectedPoint.label,
    };
  }

  private onReadingUoMError(): void {
    this.#measurementDeviceService.dataCaptureStop();
    this.currentSelectedRow = null;
    if (this.isEditMode) {
      this.cancelEdit();
    }
    this.#snackBarService.error(this.#translate.instant('surveyMeasurementTable.onReadingUoMErrorError'));
  }

  private sortDataSource(): void {
    this.dataSource = this.dataSource.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'variant' }));
  }
}
